import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { selectUser, updateUser } from '@/store/slices/authSlice';
import { useUpdateUserProfile } from '@/api/queries/auth.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/shared/AvatarWithInitials';

export default function AccountSettingsPage() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { mutate, isPending } = useUpdateUserProfile();

  const [form, setForm] = useState({ name: '', email: '' });
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });

  useEffect(() => {
    if (user) setForm({ name: user.name || '', email: user.email || '' });
  }, [user]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = () => {
    mutate({ name: form.name, email: form.email }, {
      onSuccess: () => {
        dispatch(updateUser({ name: form.name, email: form.email }));
        toast.success('Profile updated');
      },
      onError: () => toast.error('Failed to save'),
    });
  };

  const handlePwSave = () => {
    if (pwForm.next !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.next.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    mutate({ current_password: pwForm.current, new_password: pwForm.next }, {
      onSuccess: () => { toast.success('Password changed'); setPwForm({ current: '', next: '', confirm: '' }); },
      onError: () => toast.error('Incorrect current password'),
    });
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader title="Account Settings" breadcrumb={[{label:'Settings',href:'/settings'},{label:'Account'}]}/>
      <div className="card space-y-5 p-5">
        <div className="flex items-center gap-4">
          <Avatar name={user?.name || 'User'} size="xl"/>
          <div>
            <p className="text-[16px] font-bold text-[var(--text-primary)]">{user?.name}</p>
            <p className="text-[13px] text-[var(--text-secondary)]">{user?.role}</p>
          </div>
        </div>
        <hr className="border-[var(--border)]"/>
        <Input label="Full Name" value={form.name} onChange={set('name')}/>
        <Input label="Email" value={form.email} onChange={set('email')} type="email"/>
        <Input label="Phone" value={user?.phone || ''} readOnly hint="Phone number cannot be changed"/>
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Profile'}</Button>
        </div>
      </div>
      <div className="card space-y-4 p-5">
        <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">Change Password</h3>
        <Input label="Current Password" type="password" value={pwForm.current} onChange={e => setPwForm(f => ({...f, current: e.target.value}))}/>
        <Input label="New Password" type="password" value={pwForm.next} onChange={e => setPwForm(f => ({...f, next: e.target.value}))}/>
        <Input label="Confirm New Password" type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({...f, confirm: e.target.value}))}/>
        <div className="flex justify-end">
          <Button variant="outline" onClick={handlePwSave} disabled={isPending}>Change Password</Button>
        </div>
      </div>
    </div>
  );
}
